/**
 * Text-scramble reveal animation for the Atlas title (on load and on logo
 * hover). Extracted from the globe client monolith (Phase 2, PR-B).
 */
const SCRAMBLE_CHARS = '░░░░☐☑☒←→↑↓{}[]()<>|/\\\\@#$%^&*+-=_~`;:'

// Tracks the in-flight reveal per element so a new scramble cancels the prior
// one instead of running concurrent rAF loops that fight over textContent.
const activeFrames = new WeakMap<HTMLElement, number>()

interface ScrambleOptions {
  duration?: number
  startSpacing?: number
  endSpacing?: number
  color?: string | null
  /** Target text to reveal. Defaults to the element's data-text/textContent. */
  text?: string
}

/**
 * Scramble-reveals `text` (or the element's existing data-text/textContent) into
 * `el`. Reusable across the title intro and the diegetic loading status line.
 */
export function scrambleText(
  el: HTMLElement | null,
  text?: string,
  options: ScrambleOptions = {}
): void {
  if (!el) return

  const finalText = text ?? el.getAttribute('data-text') ?? el.textContent ?? ''
  // Store original text for future animations (title hover re-run).
  if (!el.getAttribute('data-text')) {
    el.setAttribute('data-text', finalText)
  }

  const length = finalText.length
  const duration = options.duration || 600
  const startSpacingVal = options.startSpacing !== undefined ? options.startSpacing : 0.15
  const endSpacingVal = options.endSpacing !== undefined ? options.endSpacing : 0.12
  const targetColor = options.color || null
  let startTime: number | null = null

  if (targetColor) {
    el.style.color = targetColor
  }

  const previousFrame = activeFrames.get(el)
  if (previousFrame !== undefined) cancelAnimationFrame(previousFrame)

  function animate(currentTime: number): void {
    if (!startTime) startTime = currentTime
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    // Cubic ease-out: fast start, slow finish
    const easedProgress = 1 - Math.pow(1 - progress, 3)
    const charsRevealed = Math.floor(easedProgress * length)

    el!.textContent = finalText
      .split('')
      .map((char, index) => {
        if (index < charsRevealed) return finalText[index]
        if (char === ' ') return ' '
        return SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)]
      })
      .join('')

    const currentSpacing = startSpacingVal + (endSpacingVal - startSpacingVal) * easedProgress
    el!.style.letterSpacing = currentSpacing + 'em'

    if (progress < 1) {
      activeFrames.set(el!, requestAnimationFrame(animate))
    } else {
      activeFrames.delete(el!)
    }
  }

  activeFrames.set(el, requestAnimationFrame(animate))
}

export function initScramble(): void {
  let animating = false

  function run(el: HTMLElement | null, options: ScrambleOptions = {}): void {
    if (!el) return
    animating = true
    const duration = options.duration || 600
    scrambleText(el, undefined, options)
    // Mirror the reveal duration to clear the hover guard once finished.
    window.setTimeout(() => {
      animating = false
    }, duration)
  }

  // Animate on load. Both the desktop ("Persona Atlas") and mobile ("Atlas")
  // logo variants carry .scramble-title; only the one visible at the current
  // breakpoint is shown, the other animates harmlessly while hidden.
  const titleEls = Array.from(document.querySelectorAll<HTMLElement>('.scramble-title'))
  titleEls.forEach((el) => run(el, { duration: 600, startSpacing: 0.15, endSpacing: 0.12 }))

  // Re-run on logo hover
  const logoLink = document.getElementById('logo-home-link')
  if (logoLink && titleEls.length > 0) {
    logoLink.addEventListener('mouseenter', () => {
      if (!animating) {
        titleEls.forEach((el) => run(el, { duration: 400, startSpacing: 0.15, endSpacing: 0.12 }))
      }
    })
  }
}
